// SPDX-License-Identifier: Elastic-2.0
/**
 * Pure helpers for the cloud sign-in form (F-613).
 *
 * Split out of `SignInForm.tsx` so that component file only exports a
 * component (keeps Fast Refresh / `react-refresh/only-export-components`
 * happy) while these stay independently unit-testable.
 */
import type { Location } from "react-router-dom";

import { ApiError } from "@/api/accounts";

import { ROUTES } from "../../routing/routes";

/** Per-field validation messages. Absent key = field is valid. */
export interface FieldErrors {
  email?: string;
  password?: string;
}

/**
 * Client-side pre-flight validation. The backend is the authority; this
 * only catches obvious mistakes before spending a round-trip. The email
 * regex is intentionally permissive — "shaped like an email", no more.
 */
export function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  const trimmed = email.trim();
  if (!trimmed) {
    errors.email = "Enter your email address.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    errors.email = "Enter a valid email address.";
  }
  if (!password) {
    errors.password = "Enter your password.";
  }
  return errors;
}

/**
 * Map a failed login into user-facing copy. Wrong email and wrong
 * password both collapse to the same credentials message (the backend
 * already does this to avoid account enumeration).
 */
export function messageForError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 401:
        return "That email or password didn't match. Try again.";
      case 403:
        return "Your email isn't verified yet. Check your inbox for the verification link.";
      case 429:
        return "Too many attempts. Wait a moment and try again.";
      case 400:
      case 422:
        return "Something went wrong with that request. Check your details and try again.";
      default:
        return "We couldn't reach Mirlai. Check your connection and try again.";
    }
  }
  return "We couldn't reach Mirlai. Check your connection and try again.";
}

/** Same-origin, root-relative paths only (open-redirect guard). */
function isSafeInternalPath(p: string | null | undefined): p is string {
  return !!p && p.startsWith("/") && !p.startsWith("//");
}

/**
 * Resolve where to send the user after a successful login:
 *   1. `?returnTo=` query param (if a safe app-local path)
 *   2. `location.state.from` (set by AuthGuard)
 *   3. `/dashboard`
 */
export function resolveReturnTo(params: URLSearchParams, location: Location): string {
  const fromQuery = params.get("returnTo");
  if (isSafeInternalPath(fromQuery)) return fromQuery;
  const fromState = (location.state as { from?: string } | null)?.from;
  if (isSafeInternalPath(fromState)) return fromState;
  return ROUTES.DASHBOARD;
}
